import PropTypes from 'prop-types';
import { Text, View } from '@react-pdf/renderer';

import { COLORS, styles } from './styles';

const IMPACT_CONFIG = {
  high: { color: COLORS.impactHigh, label: 'High' },
  medium: { color: COLORS.impactMed, label: 'Med' },
  med: { color: COLORS.impactMed, label: 'Med' },
  low: { color: COLORS.impactLow, label: 'Low' },
};

const PdfOverviewSection = ({ overview }) => {
  const {
    reviewSnippet,
    areasOfImprovement,
    whatsWorking,
    primaryAudience,
    primaryMetrics,
  } = overview;

  const hasImprovements = Array.isArray(areasOfImprovement) && areasOfImprovement.length > 0;
  const hasWhatsWorking = Array.isArray(whatsWorking) && whatsWorking.length > 0;
  const hasMetrics = Array.isArray(primaryMetrics) && primaryMetrics.length > 0;

  return (
    <View style={styles.sectionContainer}>
      <Text style={styles.sectionHeading}>
        Overview
      </Text>

      {reviewSnippet && (
        <Text style={styles.summaryText}>
          {reviewSnippet}
        </Text>
      )}

      {hasImprovements && (
        <View style={styles.table}>
          {/* Table header */}
          <View style={styles.tableHeaderRow}>
            <View style={styles.colImpact}>
              <Text style={styles.tableHeaderText}>
                Impact
              </Text>
            </View>
            <View style={styles.colIssue}>
              <Text style={styles.tableHeaderText}>
                Issue
              </Text>
            </View>
            <View style={styles.colImprove}>
              <Text style={styles.tableHeaderText}>
                What to improve
              </Text>
            </View>
          </View>

          {/* Table rows */}
          {areasOfImprovement.map((row, index) => {
            const normalizedImpact = (row.impact ?? '').toLowerCase().trim();
            const impactConfig = IMPACT_CONFIG[normalizedImpact];

            return (
              <View style={styles.tableRow} key={index}>
                <View style={styles.colImpact}>
                  <View style={styles.impactBadge}>
                    <View style={[styles.impactDot, { backgroundColor: impactConfig?.color || COLORS.textTertiary }]} />
                    <Text style={styles.impactText}>
                      {impactConfig?.label ?? row.impact}
                    </Text>
                  </View>
                </View>
                <View style={styles.colIssue}>
                  <Text style={styles.tableCellTextHeavy}>
                    {row.issue}
                  </Text>
                </View>
                <View style={styles.colImprove}>
                  <Text style={styles.tableCellTextNormal}>
                    {row.what_to_improve}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>
      )}

      {hasWhatsWorking && (
        <View style={{ marginTop: 8 }}>
          <Text style={{ fontSize: 10, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 4 }}>
            What's working
          </Text>
          <View style={styles.bulletList}>
            {whatsWorking.map((item, index) => (
              <View style={styles.bulletItem} key={index}>
                <Text style={styles.bulletDot}>
                  {'\u2022'}
                </Text>
                <Text style={styles.bulletText}>
                  {item}
                </Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {primaryAudience && (
        <View style={{ marginTop: 8 }}>
          <Text style={{ fontSize: 10, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 4 }}>
            Primary audience
          </Text>
          <Text style={styles.sectionBody}>
            {primaryAudience}
          </Text>
        </View>
      )}

      {hasMetrics && (
        <View style={{ marginTop: 8 }}>
          <Text style={{ fontSize: 10, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 4 }}>
            Primary metrics
          </Text>
          <Text style={styles.sectionBody}>
            {primaryMetrics.join('  |  ')}
          </Text>
        </View>
      )}
    </View>
  );
};

PdfOverviewSection.propTypes = {
  overview: PropTypes.shape({
    reviewSnippet: PropTypes.string,
    areasOfImprovement: PropTypes.arrayOf(PropTypes.shape({
      impact: PropTypes.string,
      issue: PropTypes.string,
      what_to_improve: PropTypes.string,
    })),
    whatsWorking: PropTypes.arrayOf(PropTypes.string),
    primaryAudience: PropTypes.string,
    primaryMetrics: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default PdfOverviewSection;
